import React from 'react';
import { createPortal } from 'react-dom';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  // Replaces the shell's default `max-w-md` cap outright (rather than
  // competing with it via `className`, where Tailwind's generation order —
  // not the order classes appear in the string — decides which same-
  // specificity `max-w-*` utility wins). Callers that don't pass this keep
  // `max-w-md` exactly as before.
  maxWidthClassName?: string;
}

/**
 * App-standard modal shell: a 16px-radius `surface` panel over a dimmed
 * overlay (see epic design_html `.modal`). Renders nothing while closed.
 *
 * Portals to `document.body` so it isn't clipped/hidden by an ancestor that
 * toggles `overflow`/`display` (e.g. a dropdown panel that closes itself the
 * moment a menu item opens this dialog).
 */
export const Modal: React.FC<ModalProps> = ({
  open,
  onClose,
  title,
  children,
  className = '',
  maxWidthClassName = 'max-w-md',
}) => {
  if (!open) {
    return null;
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        data-testid="modal-overlay"
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        className={`relative w-full ${maxWidthClassName} mx-4 rounded-2xl border border-line bg-surface p-6 text-ink shadow-xl ${className}`}
      >
        {title && <h2 className="mb-4 text-lg font-semibold text-ink">{title}</h2>}
        {children}
      </div>
    </div>,
    document.body
  );
};
